'use client'

import * as React from 'react'
import Link from 'next/link'
import { sendGTMEvent } from '@next/third-parties/google'
import { EnumMembershipPeriod } from '../../../../../../../../../../lib/database/lib/generated/enums'
import PageTitle from '@/app/(client-layout)/(public)/components/PageTitle/PageTitle'
import Price from '@/app/(client-layout)/(public)/components/Price'
import { useAppSelector } from '@/lib/store'
import { getPrices } from '@/lib/features/prices/pricesSlice'
import { getFirstActivePublicCoupon } from '@/lib/features/coupons/couponsSlice'
import { getCurrentUser } from '@/lib/features/currentUser/currentUserSlice'
import { getDiscount } from '@/lib/utils'

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface Props {}

export default ((): React.ReactElement => {
  const user = useAppSelector(getCurrentUser)
  const prices = useAppSelector(getPrices)
  const coupon = useAppSelector(getFirstActivePublicCoupon)

  const [membershipPeriod, setMembershipPeriod] = React.useState<string>(EnumMembershipPeriod.MONTHLY)

  const isAnnual = membershipPeriod === EnumMembershipPeriod.YEARLY
  const gtmItemListId = 'pricing_public'
  const gtmItemListName = 'Pricing (Public view)'

  React.useEffect(() => {
    if (!prices) {
      return
    }

    if (process.env['NEXT_PUBLIC_SVGIO_STAGE'] === 'prod') {
      sendGTMEvent({ ecommerce: null })
      sendGTMEvent({
        event: 'view_item_list',
        user_id: user?.id,
        ecommerce: {
          item_list_id: gtmItemListId,
          item_list_name: gtmItemListName,
          items: prices
            .filter((price) => price.membership_period === membershipPeriod)
            .map((price, index) => ({
              item_id: price.stripe_prod_price_id,
              item_name: `${price.membership_type?.charAt(0).toUpperCase()}${price.membership_type?.slice(1).toLowerCase()} Membership`,
              coupon: coupon?.promotion_code,
              discount: getDiscount(price, coupon),
              index,
              item_variant: `${price.membership_period?.charAt(0).toUpperCase()}${price.membership_period?.slice(1).toLowerCase()}`,
              price: Number((price.price / 100).toFixed(2)),
              promotion_id: coupon?.stripe_promo_id,
              promotion_name: coupon?.name,
              quantity: 1,
            })),
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [membershipPeriod])

  return (
    <>
      <PageTitle>Pricing</PageTitle>
      <div className="container mx-auto max-w-screen-2xl px-4 md:px-6 lg:px-8 xl:px-4">
        <div className="mx-auto mb-6 max-w-screen-md text-center lg:mb-10">
          <h2 className="mb-2 text-2xl font-bold sm:text-2xl md:text-2xl lg:text-4xl xl:text-4xl">Designed plans for teams of all sizes</h2>
          <p>Take advantage of our affordable plans whether you use it for personal or business purposes.</p>
        </div>

        <div className="m-auto mb-8 flex max-w-56 justify-center lg:mb-12">
          <div className="border-light relative flex w-full rounded-full border-2 bg-white p-1">
            <span className="pointer-events-none absolute inset-0 m-1" aria-hidden="true">
              <span
                className={`bg-primary absolute inset-0 w-1/2 rounded-full transition-transform duration-150 ease-in-out ${
                  isAnnual ? 'translate-x-full' : 'translate-x-0'
                }`}
              />
            </span>
            <button
              type="button"
              className={`relative flex-1 rounded-full p-1 text-sm transition-colors duration-150 ease-in-out ${!isAnnual ? 'text-white' : 'text-zinc-900'}`}
              onClick={() => {
                setMembershipPeriod(EnumMembershipPeriod.MONTHLY)
              }}
              aria-pressed={!isAnnual}
            >
              Monthly
            </button>
            <button
              type="button"
              className={`relative flex-1 rounded-full p-1 text-sm transition-colors duration-150 ease-in-out ${isAnnual ? 'text-white' : 'text-zinc-900'}`}
              onClick={() => {
                setMembershipPeriod(EnumMembershipPeriod.YEARLY)
              }}
              aria-pressed={isAnnual}
            >
              Yearly
            </button>
          </div>
        </div>

        <div className="mb-16 grid space-y-8 sm:gap-6 md:grid-cols-2 md:gap-8 md:space-y-0 xl:grid-cols-4 xl:gap-8">
          <Price membershipPeriod={membershipPeriod} gtmItemListId={gtmItemListId} gtmItemListName={gtmItemListName} />
          {prices
            ?.filter((price) => price.membership_period === membershipPeriod)
            .map((price) => (
              <Price
                key={price.id}
                price={price}
                membershipPeriod={membershipPeriod}
                gtmItemListId={gtmItemListId}
                gtmItemListName={gtmItemListName}
              />
            ))}
        </div>
      </div>
      <div className="mx-auto mb-4 max-w-screen-xl px-4 py-6 text-center md:px-6 lg:px-8 lg:py-12 xl:py-16">
        <h3 className="mb-2 text-2xl font-bold sm:text-2xl md:text-2xl lg:text-3xl xl:text-3xl">Solutions for your business</h3>
        <p>Efficiently manage high image volumes or integrate our API seamlessly. Let&apos;s enhance your image generation process.</p>
        <div className="mt-12 block md:flex">
          <div className="border-light bg-light mb-8 mr-0 w-full rounded border p-10 md:mb-0 md:mr-8 md:w-1/2 lg:mr-8 xl:mr-12">
            <h3 className="mb-2 text-xl font-bold">Custom plan</h3>
            <p className="mb-3">Require over 2000 images monthly for your business?</p>
            <Link
              href="/contact"
              className="bg-primary hover:bg-secondary mt-4 inline-block rounded px-5 py-2 text-white transition-all duration-300 ease-in-out"
            >
              Let&apos;s talk!
            </Link>
          </div>
          <div className="border-light bg-light w-full rounded border p-10 md:w-1/2">
            <h3 className="mb-2 text-xl font-bold">API</h3>
            <p className="mb-3">
              Ready to integrate our API for your business needs? Let&apos;s streamline your image generation process together!
            </p>
            <Link
              href={`${process.env['NEXT_PUBLIC_SVGIO_API_URL']}/v1/docs`}
              className="bg-primary hover:bg-secondary mt-4 inline-block rounded px-5 py-2 text-white transition-all duration-300 ease-in-out"
            >
              Documentation
            </Link>
          </div>
        </div>
      </div>
    </>
  )
}) satisfies React.FC<Props>
